import { computed } from 'vue';

type WebpackContext = {
  (key: string): string;
  keys(): string[];
  resolve(key: string): string;
};

type ViteContext = {
  keys: () => string[];
  resolve: (key: string) => string;
};

// Create a variable to store the webpack context if we're in webpack
// This avoids dynamic require calls that cause warnings
let webpackIconsContext: WebpackContext | null = null;
try {
  // This will only work in Webpack and will be statically analyzable
  if (typeof require !== 'undefined') {
    // @ts-ignore - Webpack specific
    webpackIconsContext = require.context('@/assets/icons', false);
  }
} catch (e) {
  // We're not in Webpack or there was an error
  webpackIconsContext = null;
}

export default function useCustomIcon() {
  const iconsContext = computed(() => {
    if (webpackIconsContext) {
      // Webpack environment - use the pre-created context
      return webpackIconsContext;
    } else {
      // Vite environment (Nuxt)
      return {
        // For Vite, we'll provide a simpler implementation
        keys: (): string[] => {
          // Return a hardcoded list of your icon files or an empty array
          return [];
        },
        // Simple wrapper to match webpack's require.context interface
        resolve: (key: string): string => `/icons/${key.replace('./', '')}`,
      };
    }
  });

  function iconPath(name: string, extension = 'svg'): string {
    if (!name) return '';
    try {
      if (!webpackIconsContext) {
        return (iconsContext.value as ViteContext).resolve(`./${name}.${extension}`);
      }
      return (iconsContext.value as WebpackContext)(`./${name}.${extension}`);
    } catch (error) {
      return '';
    }
  }

  function getCustomIconStyle(
    path: string,
    styleOptions: Record<string, any> = {},
    useMask: boolean = true
  ) {
    const url = `url(${path})`;
    return useMask
      ? {
          // Use mask styles if you want to override colors
          maskImage: url,
          webkitMaskImage: url,
          maskSize: 'contain', // change with css if needed, use !important to apply
          webkitMaskSize: 'contain',
          maskRepeat: 'no-repeat',
          webkitMaskRepeat: 'no-repeat',
          ...styleOptions,
        }
      : {
          // Use background-image to preserve original colors
          backgroundImage: url,
          backgroundSize: 'contain',
          backgroundRepeat: 'no-repeat',
          ...styleOptions,
        };
  }

  return {
    iconPath,
    getCustomIconStyle,
  };
}
